/**
 * Tenant API service.
 * Pure async functions — no React state, no hooks.
 * Swap mock implementations for real calls by changing the body of each fn.
 */
import { get, post, patch, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import type { PaginatedResponse, PaginationParams } from '@core/api/types';

// ── Domain types ──────────────────────────────────────────────────────────────

export type TenantStatus = 'active' | 'inactive' | 'suspended';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  agentCount?: number;
  /** Agents switched on (excludes inactive), across the tenant. */
  activeAgentCount?: number;
  /** Knowledge documents across the tenant's agents. */
  documentCount?: number;
  userCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface TenantStats {
  agentCount: number;
  documentCount: number;
  conversationCount: number;
  messageCount: number;
}

export interface CreateTenantPayload {
  name: string;
  slug: string;
  /** First tenant admin — invited by email (no password on happy path). */
  adminEmail: string;
  adminName?: string;
}

export interface UpdateTenantPayload {
  name?: string;
  status?: TenantStatus;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const tenantService = {
  /**
   * List all tenants (paginated).
   */
  list(params?: PaginationParams): Promise<PaginatedResponse<Tenant>> {
    return get(endpoints.tenants.list, { params });
  },

  /**
   * Get a single tenant by ID.
   */
  getById(tenantId: string): Promise<Tenant> {
    return get(endpoints.tenants.getById(tenantId));
  },

  /**
   * Get usage statistics for a tenant.
   */
  getStats(tenantId: string): Promise<TenantStats> {
    return get(endpoints.tenants.stats(tenantId));
  },

  /**
   * Create a new tenant.
   */
  create(payload: CreateTenantPayload): Promise<Tenant> {
    return post(endpoints.tenants.create, payload);
  },

  /**
   * Update tenant details.
   */
  update(tenantId: string, payload: UpdateTenantPayload): Promise<Tenant> {
    return patch(endpoints.tenants.update(tenantId), payload);
  },

  /**
   * Permanently delete a tenant and all its data.
   */
  delete(tenantId: string): Promise<void> {
    return del(endpoints.tenants.delete(tenantId));
  },

  /**
   * Suspend a tenant — blocks all agent requests.
   */
  suspend(tenantId: string): Promise<Tenant> {
    return post(endpoints.tenants.suspend(tenantId));
  },

  /**
   * Re-activate a suspended tenant.
   */
  activate(tenantId: string): Promise<Tenant> {
    return post(endpoints.tenants.activate(tenantId));
  },
} as const;
